import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Users, Mail, CheckCircle, XCircle, Award, Briefcase, 
  Send, RefreshCw, Download, ChevronDown, ChevronUp,
  TrendingUp, UserCheck, Bell, MapPin, ShieldCheck, ShieldX
} from 'lucide-react';

const CdaCandidatesAdmin = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [sendTo, setSendTo] = useState('all');
  const [verifyingId, setVerifyingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [candidatesRes, statsRes] = await Promise.all([
        axios.get('/cda/admin/candidates', { headers }),
        axios.get('/cda/admin/candidates/stats', { headers }),
      ]);
      
      setCandidates(candidatesRes.data.candidates);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load candidates');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (userId, currentlySubscribed) => {
    const action = currentlySubscribed ? 'revoke' : 'verify';
    if (!window.confirm(currentlySubscribed 
      ? 'Are you sure you want to revoke this subscription?' 
      : 'Verify this payment and grant subscription access?')) {
      return;
    }
    
    setVerifyingId(userId);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/cda/admin/verify-payment', {
        user_id: userId,
        verified: !currentlySubscribed,
        notes: currentlySubscribed ? 'Admin revoked' : 'Admin verified payment',
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(currentlySubscribed ? 'Subscription revoked' : 'Payment verified & access granted!');
      fetchData();
    } catch (err) {
      console.error('Failed to verify payment:', err);
      toast.error('Failed to update subscription status');
    } finally {
      setVerifyingId(null);
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Signed Up', 'Subscribed', 'Progress', 'Readiness Score', 'Newsletter', 'Placement'];
    const rows = candidates.map(c => [
      c.name,
      c.email,
      c.created_at ? new Date(c.created_at).toLocaleDateString() : '',
      c.subscribed ? 'Yes' : 'No',
      `${c.steps_completed}/12`,
      c.readiness_score || '',
      c.newsletter_opted_in ? 'Yes' : 'No',
      c.placement_requested ? 'Yes' : 'No',
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cda-candidates-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported!');
  };

  const sendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterContent.trim()) {
      toast.error('Please enter subject and content');
      return;
    }
    
    setSendingNewsletter(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/cda/admin/newsletter/send', {
        subject: newsletterSubject,
        html_content: newsletterContent,
        send_to: sendTo,
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Newsletter sent to ${res.data.sent} recipients!`);
      setShowNewsletter(false);
      setNewsletterSubject('');
      setNewsletterContent('');
    } catch (err) {
      console.error('Failed to send newsletter:', err);
      toast.error(err.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSendingNewsletter(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color = 'text-[#0d7d6f]' }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#0d7d6f] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">CDA Candidates</h1>
            <p className="text-sm text-slate-500">Manage your Care Comply candidates</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={() => setShowNewsletter(!showNewsletter)} className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="compose-newsletter-btn">
              <Mail className="w-4 h-4 mr-2" /> Compose Newsletter
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stats-grid">
            <StatCard icon={Users} label="Total Signups" value={stats.total_signups} />
            <StatCard icon={UserCheck} label="Active Subscribers" value={stats.active_subscribers} color="text-green-600" />
            <StatCard icon={Bell} label="Newsletter Opted-In" value={stats.newsletter_subscribers} color="text-blue-600" />
            <StatCard icon={Award} label="Completed Journey" value={stats.completed_journey} color="text-purple-600" />
            <StatCard icon={MapPin} label="Placement Requests" value={stats.placement_requests} color="text-orange-600" />
          </div>
        )}

        {/* Newsletter Composer */}
        {showNewsletter && (
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="newsletter-composer">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-[#0d7d6f]" /> Compose Newsletter
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Send To</label>
                <select 
                  value={sendTo} 
                  onChange={(e) => setSendTo(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="newsletter-send-to"
                >
                  <option value="all">All Newsletter Subscribers</option>
                  <option value="subscribers_only">Paid Subscribers Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                <Input 
                  value={newsletterSubject} 
                  onChange={(e) => setNewsletterSubject(e.target.value)}
                  placeholder="Your monthly CDA update from Jazmin..."
                  data-testid="newsletter-subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content (HTML)</label>
                <textarea 
                  value={newsletterContent} 
                  onChange={(e) => setNewsletterContent(e.target.value)}
                  placeholder="<h1>Hello from Jazmin!</h1><p>Here's what's new this month...</p>"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm min-h-[200px] font-mono"
                  data-testid="newsletter-content"
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={sendNewsletter} disabled={sendingNewsletter} className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="send-newsletter-btn">
                  {sendingNewsletter ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {sendingNewsletter ? 'Sending...' : 'Send Newsletter'}
                </Button>
                <Button variant="outline" onClick={() => setShowNewsletter(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Candidates Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="candidates-table">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">All Candidates ({candidates.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Signed Up</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Paid</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Readiness</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {candidates.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`candidate-row-${c.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-400">
                        {c.newsletter_opted_in && <Bell className="w-3 h-3 inline text-blue-500 mr-1" title="Newsletter" />}
                        {c.placement_requested && <MapPin className="w-3 h-3 inline text-orange-500" title="Placement requested" />}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{c.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {c.subscribed ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          <XCircle className="w-3 h-3 mr-1" /> Unpaid
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        c.steps_completed >= 12 ? 'bg-green-100 text-green-800' :
                        c.steps_completed >= 6 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {c.steps_completed}/12
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {c.readiness_score != null ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          c.readiness_score >= 80 ? 'bg-green-100 text-green-800' :
                          c.readiness_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {c.readiness_score}%
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <Button
                        size="sm"
                        variant={c.subscribed ? "outline" : "default"}
                        onClick={() => verifyPayment(c.id, c.subscribed)}
                        disabled={verifyingId === c.id}
                        className={c.subscribed ? "text-red-600 hover:bg-red-50 border-red-200" : "bg-[#0d7d6f] hover:bg-[#0a6358]"}
                        data-testid={`verify-btn-${c.id}`}
                      >
                        {verifyingId === c.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : c.subscribed ? (
                          <><ShieldX className="w-4 h-4 mr-1" /> Revoke</>
                        ) : (
                          <><ShieldCheck className="w-4 h-4 mr-1" /> Verify Payment</>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      No candidates yet. They'll appear here when they sign up!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CdaCandidatesAdmin;
